import { useMemo } from "react";
import { EXCHANGES } from "@shared/common/constants";
import { isSymbol } from "@shared/types/utils";
import { sharedLib } from "@shared/services/shared.service";
import { useSymbolsMetadata } from "@shared/utils/hooks/misc";
import { sortByExchangeOrder } from "@shared/utils/misc";
import { skipToken } from "@reduxjs/toolkit/query";
import { useDefaultExchangeSymbol } from "@shared/features/bots/BotForm/common/hooks";
import { BasicBotData, BotData, BotType, ClientData } from "@shared/types";
import {
  BotFormType,
  MultiValue,
} from "@shared/features/bots/BotForm/common/types";

interface useExchangeToSymbolPricesQueriesProps {
  account?: ClientData;
  accounts: ClientData[] | MultiValue;
  exchanges?: string[] | MultiValue;
  symbols: string[];
  useSymbolFromAlert?: boolean | MultiValue;
  botType: BotType;
  botFormType?: BotFormType;
  isChannelConfiguration?: boolean;
  isSymbolValid?: boolean;
  botsData?: BotData[] | BasicBotData[] | undefined;
}

const useExchangeToSymbolPricesQueries = ({
  account,
  symbols,
  useSymbolFromAlert,
  accounts,
  exchanges,
  botType,
  botFormType,
  isChannelConfiguration,
  isSymbolValid,
  botsData,
}: useExchangeToSymbolPricesQueriesProps) => {
  const { isSymbolActiveInExchange } = useSymbolsMetadata();
  const { getDefaultSymbol } = useDefaultExchangeSymbol();

  const isSignalsBot = botType === BotType.SIGNAL;
  const isTradingViewSignalsBot = botType === BotType.TRADING_VIEW_SIGNALS;
  const isBulkCreate = botFormType === BotFormType.BULK_CREATE;
  const isBulkEdit = botFormType === BotFormType.BULK_EDIT;
  const symbol = symbols?.[0];

  const getExchangeForSymbol = (exchanges?: string[], symbol?: string) => {
    if (!exchanges || !symbol) return;
    const sortedExchanges = sortByExchangeOrder(exchanges);
    return sortedExchanges?.find((exchange) =>
      isSymbolActiveInExchange(symbol, exchange)
    );
  };

  const exchangeSymbolPriceArg = useMemo(() => {
    if (isChannelConfiguration) return skipToken;
    if (isSignalsBot)
      return { exchange: EXCHANGES.BINANCE, symbol: "BTC/USDT" };
    else if (account && symbols.length > 0 && isSymbolValid)
      return { exchange: account.exchange, symbol: symbol };
    else if (
      isTradingViewSignalsBot &&
      !isSymbol(exchanges) &&
      exchanges?.length
    ) {
      const validExchangeForSymbol = getExchangeForSymbol(exchanges, symbol);
      if (validExchangeForSymbol) {
        return { exchange: validExchangeForSymbol, symbol };
      }
    }
    return skipToken;
  }, [
    isChannelConfiguration,
    botType,
    account,
    symbols,
    isSymbolValid,
    exchanges,
  ]);

  const getExchangeSymbolPriceQuery =
    sharedLib.sharedApi.bots.useGetExchangeSymbolPriceQuery(
      exchangeSymbolPriceArg
    );

  const getExchangeToSymbols = () => {
    if (isBulkEdit) {
      return botsData?.reduce((acc: Record<string, string[]>, botData) => {
        let symbol = botData.symbols?.[0];
        let validExchangeForSymbol: string | undefined;
        const useFormExchanges =
          isTradingViewSignalsBot && !isSymbol(exchanges);
        if (!symbol) {
          let _exchanges: string[] | undefined;
          if (useFormExchanges) _exchanges = exchanges;
          else if (botData.exchange) _exchanges = [botData.exchange];
          else _exchanges = botData.exchanges;
          const sortedExchanges = sortByExchangeOrder(_exchanges);
          validExchangeForSymbol = sortedExchanges?.[0];
          symbol = getDefaultSymbol(sortedExchanges?.[0]);
        } else if (isTradingViewSignalsBot && !isSymbol(exchanges)) {
          validExchangeForSymbol = exchanges?.[0];
        } else {
          validExchangeForSymbol =
            botData.exchange ?? getExchangeForSymbol(botData.exchanges, symbol);
        }
        if (validExchangeForSymbol && symbol) {
          if (!(validExchangeForSymbol in acc)) {
            acc[validExchangeForSymbol] = [symbol];
          } else if (!acc[validExchangeForSymbol].includes(symbol)) {
            acc[validExchangeForSymbol].push(symbol);
          }
        }
        return acc;
      }, {});
    } else if (isBulkCreate) {
      const parsedAccounts =
        isSymbol(accounts) || !accounts?.length ? undefined : accounts;
      return parsedAccounts?.reduce(
        (acc: Record<string, string[]>, account) => {
          const { exchange } = account;
          const exchangeSymbol = useSymbolFromAlert
            ? getDefaultSymbol(exchange)
            : symbol;
          if (exchangeSymbol && !(exchange in acc)) {
            acc[exchange] = [exchangeSymbol];
          }
          return acc;
        },
        {}
      );
    }
  };

  const getExchangeToSymbolPricesRequest = useMemo(() => {
    const exchangeToSymbols = getExchangeToSymbols();
    if (
      isSignalsBot ||
      !exchangeToSymbols ||
      !Object.keys(exchangeToSymbols).length
    )
      return skipToken;
    return {
      exchange_to_symbols: exchangeToSymbols,
    };
  }, [
    symbols,
    accounts,
    botType,
    botFormType,
    exchanges,
    getDefaultSymbol,
    useSymbolFromAlert,
  ]);

  const getExchangeToSymbolPricesQuery =
    sharedLib.sharedApi.bots.useGetExchangeToSymbolPricesQuery(
      getExchangeToSymbolPricesRequest
    );

  return {
    getExchangeSymbolPriceQuery,
    getExchangeToSymbolPricesQuery,
  };
};

export default useExchangeToSymbolPricesQueries;
